<template>
  <VendorBrandAdmin ref="vendorEditor" :api-base="apiBase" />
</template>

<script>
/**
 * BrandOperatorAdmin (Musubi) — the OPERATOR / platform-curator brand editor.
 * COMPOSES the vendor `@apexl/icarus-branding` BrandAdmin (rather than
 * `extends`-ing it) so the FULL vendor surface renders unchanged (Assets /
 * Tokens / Templates / Preview / Render / Governance) for operators editing
 * the platform-default brand (tenant_id = NULL), and adds the
 * deep-link-to-a-surface entry point the plain vendor component lacks.
 *
 * F-TT-01 fix (2026-07-15): this used to `extends: VendorBrandAdmin` with no
 * `<template>`/`<style scoped>` of its own. Vue only stamps a component's own
 * scope id (`data-v-*`) onto elements rendered by ITS OWN `<template>` block;
 * `extends` inherits options (data/methods/render) but the child SFC here had
 * no template, so Vue fell back to rendering with the vendor's *inherited*
 * render function while the DOM ended up tagged with the wrapper's scope id
 * instead of the vendor's. Every vendor `.ba-*` scoped rule (`.ba-split`,
 * `.ba-live-iframe{height:480px}`, `.ba-iframe{height:360px}`, the gallery
 * overlay, the template-library pills) silently failed to match, so the
 * email/pdf live-preview iframe collapsed to the browser's intrinsic
 * 300×150. Mounting `<VendorBrandAdmin>` as a real child component instead
 * means the vendor SFC renders (and scopes) itself — its own `data-v-*` id
 * lands on its own DOM, and every `.ba-*` rule is alive again.
 *
 * This is the component the `BrandAdmin` wrapper mounts when GET
 * /api/v1/branding/surface reports `scope: 'operator'`. Tenants never reach it —
 * they get the cut-down TenantBrandSurface / TenantTemplateEditor instead, which
 * is how the render-servers + governance tabs are hidden from non-operators
 * (they simply aren't part of the tenant components).
 *
 * The deep-link behaviour is unchanged from the previous BrandAdmin override:
 * on mount, an `initialSurface` prop (or `?surface=` query) jumps to the
 * Templates tab and opens that surface's default template — driven via
 * `$refs.vendorEditor` since the vendor instance is now a real child, not
 * `this` itself.
 *
 * FOLLOW-UP (not done in this pass — minimum honest fix only): operators
 * still get the vendor's own live-preview gating (`livePreviewSupported`),
 * i.e. the big split preview only shows for email/pdf surfaces; other
 * surfaces still show "Live preview supports email + pdf — use the Preview
 * tab." Giving operators the tenant editor's always-visible split treatment
 * (reusing `TenantTemplateEditor.vue`'s layout) is a separate, larger rework
 * flagged by the audit and intentionally deferred.
 */
import { BrandAdmin as VendorBrandAdmin } from '@apexl/icarus-branding';

export default {
  name: 'BrandOperatorAdmin',
  components: { VendorBrandAdmin },
  props: {
    apiBase: { type: String, default: '/api/v1' },
    initialSurface: { type: String, default: '' },
  },
  mounted() {
    const surface = this.initialSurface || this.$route?.query?.surface || '';
    const ed = this.$refs.vendorEditor;
    if (!ed) return;

    if (surface && typeof ed.openTemplate === 'function') {
      ed.tab = 'templates';
      ed.openTemplate(surface);
    }

    // Sticky-banner fix (reported live, 2026-08-13): the vendor editor SETS
    // `error` in every catch block but never CLEARS it, so one failed call
    // pinned a red "Failed to open template." banner to the page for the rest
    // of the session — long after the user had moved on to a surface that
    // opened perfectly well. The tester read it as a permanent page-load
    // failure.
    //
    // `sel` is reassigned (a brand-new object) ONLY on openTemplate()'s
    // success path, so an identity change here is proof the latest
    // user-initiated action worked — which is exactly when the banner should
    // go. Watching from the wrapper keeps the vendor SFC untouched; the
    // backend half of the same report (proposal_export 500ing out of
    // BrandTemplateDefaults) is fixed at the root in
    // api/patches/io-branding-proposal-export-default-blocks.patch.
    this.$watch(() => ed.sel, () => { ed.error = ''; });
  },
};
</script>
